import React, { useEffect, useState } from "react";
import countrySeal from "../assets/images/country-seal.jpg";
import mlgLogo from "../assets/images/mlg-logo.png";
import { certificateService } from "../services/api/certificate.service";
import { paymentService } from "../services/api/payment.service";
import { UserStatusId } from "../types/user.types";
import type { AuthoritySignature } from "../types/certificate.types";
import { getFullPath } from "../utils/getFullPath";

export interface DivorceCertProps {
  plaintiffName: string;
  plaintiffDateOfBirth: string;
  plaintiffPlaceOfBirth: string;
  plaintiffCountyOfBirth: string;
  plaintiffFatherName: string;
  plaintiffMotherName: string;
  defendantName: string;
  defendantDateOfBirth: string;
  defendantPlaceOfBirth: string;
  defendantCountyOfBirth: string;
  defendantFatherName: string;
  defendantMotherName: string;
  placeOfWedding?: string;
  dateOfWedding?: string;
  countyName?: string;
  certificateCode?: string;
  revenueNumber?: string;
  ministryName?: string;
  plaintiffPhotoUrl?: string;
  defendantPhotoUrl?: string;
  certificateApplicationId?: number;
}

export const DivorceCert: React.FC<DivorceCertProps> = ({
  plaintiffName,
  defendantName,
  plaintiffDateOfBirth: _plaintiffDateOfBirth,
  plaintiffPlaceOfBirth: _plaintiffPlaceOfBirth,
  plaintiffCountyOfBirth: _plaintiffCountyOfBirth,
  plaintiffFatherName: _plaintiffFatherName,
  plaintiffMotherName: _plaintiffMotherName,
  defendantDateOfBirth: _defendantDateOfBirth,
  defendantPlaceOfBirth: _defendantPlaceOfBirth,
  defendantCountyOfBirth: _defendantCountyOfBirth,
  defendantFatherName: _defendantFatherName,
  defendantMotherName: _defendantMotherName,
  placeOfWedding: _placeOfWedding = "",
  dateOfWedding = "",
  countyName: _countyName = "",
  certificateCode = "",
  revenueNumber = "",
  ministryName = "",
  plaintiffPhotoUrl,
  defendantPhotoUrl,
  certificateApplicationId,
}) => {
  const [authoritySignatures, setAuthoritySignatures] = useState<
    AuthoritySignature[]
  >([]);
  const [paymentRevenueNumber, setPaymentRevenueNumber] = useState("");
  const [paymentReferenceNumber, setPaymentReferenceNumber] = useState("");

  const formatDate = (dateStr: string) => {
    if (!dateStr) return { day: "", month: "", year: "" };
    try {
      const date = new Date(dateStr);
      return {
        day: date.getDate().toString(),
        month: date.toLocaleDateString("en-US", { month: "long" }),
        year: date.getFullYear().toString(),
      };
    } catch {
      return { day: "", month: "", year: "" };
    }
  };

  const divorceDate = formatDate(dateOfWedding);
  const cleanPlaintiffName = plaintiffName;
  const cleanDefendantName = defendantName;

  const plaintiffPhotoFullUrl = getFullPath(plaintiffPhotoUrl);
  const defendantPhotoFullUrl = getFullPath(defendantPhotoUrl);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!certificateApplicationId) {
        setPaymentRevenueNumber(revenueNumber || "");
        setPaymentReferenceNumber("");
        return;
      }

      try {
        const payments = await paymentService.getPaymentsByApplicationId(
          certificateApplicationId,
        );
        const payment = Array.isArray(payments)
          ? payments.find((p) => p.revenueNumber || p.paymentReferenceNumber) ||
            payments[0]
          : undefined;

        setPaymentRevenueNumber(payment?.revenueNumber || revenueNumber || "");
        setPaymentReferenceNumber(payment?.paymentReferenceNumber || "");
      } catch (err) {
        console.error("Error fetching payment details:", err);
        setPaymentRevenueNumber(revenueNumber || "");
        setPaymentReferenceNumber("");
      }
    };

    fetchPaymentDetails();
  }, [certificateApplicationId, revenueNumber]);

  const getAuthoritySignatureSrc = (
    signature?: AuthoritySignature,
  ): string | null => {
    if (!signature?.signature) return null;
    const value = signature.signature.trim();
    if (!value) return null;
    if (value.startsWith("data:image/")) return value;
    return `data:image/png;base64,${value}`;
  };

  const getAuthoritySignatureUrl = (index: number): string | null => {
    return getAuthoritySignatureSrc(authoritySignatures[index]);
  };

  const redInterlockedXBorder =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'%3E%3Crect width='28' height='28' fill='none'/%3E%3Cg stroke='%23b31234' stroke-width='1.8' fill='none' stroke-linecap='round'%3E%3Ccircle cx='14' cy='14' r='7.5'/%3E%3Cline x1='10' y1='10' x2='18' y2='18'/%3E%3Cline x1='18' y1='10' x2='10' y2='18'/%3E%3Ccircle cx='0.5' cy='14' r='7.5'/%3E%3Ccircle cx='27.5' cy='14' r='7.5'/%3E%3C/g%3E%3C/svg%3E\") 10 round";

  useEffect(() => {
    const fetchAuthoritySignatures = async () => {
      if (!certificateApplicationId) {
        setAuthoritySignatures([]);
        return;
      }

      try {
        const data = await certificateService.getAuthoritySignatures(
          certificateApplicationId,
        );
        setAuthoritySignatures(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to fetch authority signatures:", error);
        setAuthoritySignatures([]);
      }
    };

    fetchAuthoritySignatures();
  }, [certificateApplicationId]);

  useEffect(() => {
    const handleBeforePrint = async () => {
      if (certificateApplicationId) {
        try {
          await certificateService.changeStatus(
            certificateApplicationId,
            UserStatusId.Completed,
          );
        } catch (error) {
          console.error(
            "Failed to update certificate status to Completed:",
            error,
          );
        }
      }
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, [certificateApplicationId]);

  return (
    <div
      style={{
        width: "297mm",
        minHeight: "210mm",
        margin: "0 auto",
        padding: "15mm",
        background: "#d4d4d4",
        fontFamily: "'Times New Roman', serif",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "5mm",
          left: "10mm",
          right: "10mm",
          bottom: "5mm",
          border: "8px solid #b31234",
          borderRadius: "4px",
          background: "#c8c8c8",
          borderImage: redInterlockedXBorder,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            right: "12px",
            bottom: "12px",
            border: "8px solid #b31234",
            background: "linear-gradient(to bottom, #ffffff, #ffffff)",
            padding: "25px",
            borderImage: redInterlockedXBorder,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "15px",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: "15%",
                    verticalAlign: "top",
                    padding: "10px 5px",
                  }}
                >
                  <img
                    src={countrySeal || undefined}
                    alt="Country Seal"
                    style={{
                      width: "80px",
                      height: "80px",
                      margin: "0 auto",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                  <div
                    style={{
                      marginTop: "6px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {certificateCode || ""}
                  </div>
                  <div
                    style={{
                      marginTop: "3px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    LRA REF NO. {paymentRevenueNumber || ""}
                  </div>
                  <div
                    style={{
                      marginTop: "2px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    PAYMENT REF. {paymentReferenceNumber || ""}
                  </div>
                </td>
                <td style={{ textAlign: "center", paddingBottom: "10px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "15px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td colSpan={3}>
                          <h1
                            style={{
                              fontSize: "24px",
                              fontWeight: "bold",
                              margin: "0px 0",
                              letterSpacing: "2px",
                              textTransform: "uppercase",
                            }}
                          >
                            {ministryName}
                          </h1>
                        </td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>
                          {plaintiffPhotoFullUrl ? (
                            <img
                              src={plaintiffPhotoFullUrl || undefined}
                              alt="Plaintiff"
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                margin: "0 auto",
                                display: "block",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                backgroundColor: "#6c6d7c",
                                margin: "0 auto",
                              }}
                            />
                          )}
                        </td>
                        <td
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                          }}
                        >
                          <p style={{ margin: "3px 0" }}>
                            Capitol Hill, Executive Mansion Grounds
                          </p>
                          <p style={{ margin: "3px 0" }}>P.O. Box 9008</p>
                          <p style={{ margin: "3px 0" }}>
                            1000 Monrovia, 10 Liberia
                          </p>
                          <p style={{ margin: "3px 0" }}>West Africa</p>
                        </td>
                        <td>
                          {defendantPhotoFullUrl ? (
                            <img
                              src={defendantPhotoFullUrl || undefined}
                              alt="Defendant"
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                margin: "0 auto",
                                display: "block",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100px",
                                height: "100px",
                                border: "0px solid #999",
                                backgroundColor: "#6c6d7c",
                                margin: "0 auto",
                              }}
                            />
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={3}>
                          <h2
                            style={{
                              fontSize: "24px",
                              margin: "6px 0",
                              letterSpacing: "1px",
                              fontWeight: "bold",
                            }}
                          >
                            TRADITIONAL DIVORCE CERTIFICATE
                          </h2>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
                <td
                  style={{
                    width: "15%",
                    verticalAlign: "top",
                    padding: "10px 5px",
                  }}
                >
                  <img
                    src={mlgLogo || undefined}
                    alt="MLG Logo"
                    style={{
                      width: "80px",
                      height: "80px",
                      margin: "0 auto",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </td>
              </tr>

              <tr>
                <td
                  colSpan={3}
                  style={{
                    padding: "5px 0",
                    textAlign: "justify",
                    lineHeight: "2.05",
                    fontSize: "14px",
                  }}
                >
                  <p style={{ margin: "-10px 0" }}>
                    To ALL WHOM THESE PRESENTS DO COME: Know Ye that, whereas,
                    it does appear to the satisfaction of the Traditional
                    Appellate Court of this Ministry, (MoLG) and the Hearing
                    Board upon application of
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {` ${cleanPlaintiffName} `}
                    </span>
                    Plaintiff for Traditional Certificate of Divorcement from
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {` ${cleanDefendantName} `}
                    </span>
                    Defendant, it was proven that there are cogent legal reasons
                    for the granting of this divorce.
                  </p>

                  <p style={{ margin: "8px 0" }}>
                    NOW, THEREFORE, the Matrimonial Relationship or Civil
                    Contract made and entered into this
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {` ${divorceDate.day} `}
                    </span>
                    day of
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {` ${divorceDate.month} `}
                    </span>
                    , A.D.
                    <span
                      style={{
                        textDecoration: "underline",
                        fontFamily: "'Dancing Script', cursive",
                      }}
                    >
                      {` ${divorceDate.year} `}
                    </span>
                    between the parties herein mentioned supra; having been
                    dissolved by a Final Judgement of our Traditional Appellate
                    Court, it is hereby declared Void Ab initio to all intents
                    and purposes as if no such relationship ever existed between
                    them in the past.
                  </p>

                  <p style={{ margin: "8px 0" }}>
                    And that both parties henceforth be held and regarded as two
                    separate, decoupled, unyoked, dissociated, disjointed and
                    distinct persons, altogether disconnected by any mystical
                    UNION or Civil Contract or Marriage heretofore made and
                    entered into between them.
                  </p>
                </td>
              </tr>

              <tr>
                <td colSpan={3}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                      marginTop: "-25px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td style={{ width: "70%", padding: "10px" }}>
                          <p style={{ margin: "5px 0", fontSize: "15px" }}>
                            Date of Divorce:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-block",
                                minWidth: "250px",
                              }}
                            >
                              {` ${divorceDate.day} ${divorceDate.month} ${divorceDate.year} `}
                            </span>
                          </p>
                        </td>
                        <td style={{ width: "30%", padding: "10px" }}>
                          <p style={{ margin: "5px 0" }}>
                            Given under our hands and seal of MoLG.
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            In the City of Monrovia, Montserrado County,
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            R.L., This ____ Day of __________ A.D. 20____.
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>

              <tr>
                <td colSpan={3}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                      marginTop: "20px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            width: "33%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Prepared:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-flex",
                                minWidth: "200px",
                                minHeight: "40px",
                                alignItems: "flex-end",
                                justifyContent: "center",
                              }}
                            >
                              {getAuthoritySignatureUrl(0) && (
                                <img
                                  src={
                                    getFullPath(getAuthoritySignatureUrl(0)) ||
                                    undefined
                                  }
                                  alt="Superintendent Signature"
                                  style={{
                                    maxHeight: "38px",
                                    maxWidth: "190px",
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            SUPERINTENDENT/TRIBAL AFFAIRS
                          </p>
                        </td>

                        <td
                          style={{
                            width: "33%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Verified/Attested:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-flex",
                                minWidth: "200px",
                                minHeight: "40px",
                                alignItems: "flex-end",
                                justifyContent: "center",
                              }}
                            >
                              {getAuthoritySignatureUrl(1) && (
                                <img
                                  src={
                                    getFullPath(getAuthoritySignatureUrl(1)) ||
                                    undefined
                                  }
                                  alt="Assistant Minister Signature"
                                  style={{
                                    maxHeight: "38px",
                                    maxWidth: "190px",
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            ASSISTANT MINISTER/LEGAL AAFFAIRS
                          </p>
                        </td>
                        <td
                          style={{
                            width: "34%",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <p style={{ margin: "5px 0" }}>
                            Approved:{" "}
                            <span
                              style={{
                                borderBottom: "1px solid #000",
                                display: "inline-flex",
                                minWidth: "230px",
                                minHeight: "40px",
                                alignItems: "flex-end",
                                justifyContent: "center",
                              }}
                            >
                              {getAuthoritySignatureUrl(2) && (
                                <img
                                  src={
                                    getFullPath(getAuthoritySignatureUrl(2)) ||
                                    undefined
                                  }
                                  alt="Deputy Minister Signature"
                                  style={{
                                    maxHeight: "38px",
                                    maxWidth: "220px",
                                    objectFit: "contain",
                                  }}
                                />
                              )}
                            </span>
                          </p>
                          <p
                            style={{
                              textAlign: "center",
                              fontWeight: "bold",
                              marginTop: "5px",
                            }}
                          >
                            DEPUTY MINISTER/ADMINISTRATOR
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
              <tr style={{ textAlign: "left" }}>
                <td
                  colSpan={3}
                  style={{ padding: "1px 0 0 0", textAlign: "left" }}
                >
                  {certificateCode ? (
                    <img
                      src={
                        getFullPath(
                          `https://api.qrserver.com/v1/create-qr-code/?size=75x75&data=${encodeURIComponent(`CODE:${certificateCode}|LRA:${paymentRevenueNumber}|PAYMENT:${paymentReferenceNumber}`)}`,
                        ) || undefined
                      }
                      alt={`QR Code: ${certificateCode}`}
                      style={{
                        width: "75px",
                        height: "75px",
                        border: "1px solid #433d3d",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "75px",
                        height: "75px",
                        border: "1px solid #433d3d",
                        display: "inline-block",
                      }}
                    />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default DivorceCert;
